import React, { useState, useCallback, useRef } from "react";
import { useDropzone } from "react-dropzone";
import * as pdfjsLib from "pdfjs-dist";
import JobMatcher from "./JobMatcher";
import { getAI, getGenerativeModel, GoogleAIBackend } from "firebase/ai";
import { app } from "../config/firebase";
import {
  CircularProgressbarWithChildren,
  buildStyles,
} from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import {
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaLinkedin,
  FaGithub,
  FaLink,
} from "react-icons/fa";

// Set worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const INDUSTRY_ROLES = {
  "Software Development": [
    "Java Backend Developer",
    "Frontend Developer",
    "Full Stack Developer",
    "Python Developer",
    "Mobile App Developer",
    "DevOps Engineer",
    "Cloud Engineer",
  ],
  "Data & Analytics": [
    "Data Scientist",
    "Data Analyst",
    "Business Intelligence Analyst",
    "Data Engineer",
    "Machine Learning Engineer",
    "Big Data Engineer",
  ],
  "Design & Creative": [
    "UI/UX Designer",
    "Product Designer",
    "Graphic Designer",
    "Visual Designer",
    "Motion Designer",
    "Brand Designer",
  ],
  "Marketing & Sales": [
    "Digital Marketing Manager",
    "Content Marketing Specialist",
    "SEO Specialist",
    "Sales Manager",
    "Business Development Manager",
    "Product Marketing Manager",
  ],
  "Management & Leadership": [
    "Project Manager",
    "Product Manager",
    "Engineering Manager",
    "Technical Lead",
    "Scrum Master",
    "Program Manager",
  ],
  "Finance & Accounting": [
    "Financial Analyst",
    "Accountant",
    "Investment Banker",
    "Financial Controller",
    "Risk Analyst",
    "Financial Manager",
  ],
  "Healthcare & Medical": [
    "Medical Doctor",
    "Nurse Practitioner",
    "Healthcare Administrator",
    "Clinical Research Coordinator",
    "Pharmacist",
    "Medical Technologist",
  ],
  "Operations & Logistics": [
    "Operations Manager",
    "Supply Chain Manager",
    "Logistics Coordinator",
    "Warehouse Manager",
    "Quality Assurance Manager",
    "Production Manager",
  ],
};

const ResumeAnalyzer = () => {
  const [selectedRole, setSelectedRole] = useState("");
  const [resumeContent, setResumeContent] = useState({
    skills: "",
    experience: "",
    projects: "",
    education: "",
  });
  const [currentStep, setCurrentStep] = useState(1);
  const [experience, setExperience] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const lastAiRequest = useRef({ text: "", prompt: "", result: null });

  // PDF reading with line grouping
  const readPdfFile = async (file) => {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    let fullText = "";

    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const textContent = await page.getTextContent();

      // Group items by their y position (line)
      const lines = {};
      textContent.items.forEach((item) => {
        const y = Math.round(item.transform[5]);
        if (!lines[y]) lines[y] = [];
        lines[y].push({ x: item.transform[4], str: item.str });
      });

      // Sort lines by y (top to bottom)
      const sortedY = Object.keys(lines)
        .map(Number)
        .sort((a, b) => b - a);

      // For each line, sort words by x (left to right), then join
      const pageText = sortedY
        .map((y) =>
          lines[y]
            .sort((a, b) => a.x - b.x)
            .map((item) => item.str)
            .join(" ")
        )
        .join("\n");

      fullText += pageText + "\n";
    }

    return fullText;
  };

  // Section extraction
  const extractSections = (text) => {
    const sections = {
      skills: "",
      experience: "",
      projects: "",
      education: "",
    };

    const skillsMatch = text.match(
      /SKILLS?(.*?)(?=EXPERIENCE|PROJECTS?|EDUCATION|$)/is
    );
    const experienceMatch = text.match(
      /EXPERIENCE(.*?)(?=SKILLS?|PROJECTS?|EDUCATION|$)/is
    );
    const projectsMatch = text.match(
      /PROJECTS?(.*?)(?=SKILLS?|EXPERIENCE|EDUCATION|$)/is
    );
    const educationMatch = text.match(
      /EDUCATION(.*?)(?=SKILLS?|EXPERIENCE|PROJECTS?|$)/is
    );

    sections.skills = skillsMatch ? skillsMatch[1].trim() : "";
    sections.experience = experienceMatch ? experienceMatch[1].trim() : "";
    sections.projects = projectsMatch ? projectsMatch[1].trim() : "";
    sections.education = educationMatch ? educationMatch[1].trim() : "";

    return sections;
  };

  // User details extraction
  const extractUserDetails = (text) => {
    const userDetails = {
      name: "",
      email: "",
      phone: "",
      location: "",
      linkedin: "",
      github: "",
      portfolio: "",
      summary: "",
      languages: [],
      certifications: [],
      skills: [],
    };

    const lines = text
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line);

    // Name extraction
    for (let i = 0; i < Math.min(lines.length, 5); i++) {
      const line = lines[i];
      if (
        line.length > 5 &&
        !line.match(/@|\.com|github\.com|linkedin\.com|\d{3,}/i) &&
        line.match(/^[A-Z][a-zA-Z\s.'-]+(\s[A-Z][a-zA-Z\s.'-]+)*$/)
      ) {
        userDetails.name = line;
        break;
      }
    }
    if (!userDetails.name && lines.length > 0) {
      if (
        !lines[0].match(
          /^(RESUME|CURRICULUM VITAE|CONTACT|SUMMARY|PROFILE|SKILLS|EXPERIENCE|EDUCATION)/i
        ) &&
        !lines[0].match(/@|\.com|github\.com|linkedin\.com|\d{3,}/i) &&
        lines[0].length < 50
      ) {
        userDetails.name = lines[0];
      }
    }

    // Contact info
    const patterns = {
      email: /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9._-]+)/i,
      phone: /(\+?\d{1,3}[-.\s]?\d{3,5}[-.\s]?\d{5,8}|\d{10,12})/,
      linkedin: /(linkedin\.com\/in\/[a-zA-Z0-9-]+)/i,
      github: /(github\.com\/[a-zA-Z0-9-]+)/i,
      location:
        /([A-Z][a-zA-Z\s]+,\s*[A-Z]{2}|[A-Z][a-zA-Z\s]+,\s*[A-Z][a-zA-Z\s]+)/,
    };

    for (const line of lines) {
      const emailMatch = line.match(patterns.email);
      if (emailMatch && !userDetails.email) userDetails.email = emailMatch[1];
      const phoneMatch = line.match(patterns.phone);
      if (phoneMatch && !userDetails.phone) userDetails.phone = phoneMatch[1];
      const linkedinMatch = line.match(patterns.linkedin);
      if (linkedinMatch && !userDetails.linkedin)
        userDetails.linkedin = linkedinMatch[1];
      const githubMatch = line.match(patterns.github);
      if (githubMatch && !userDetails.github)
        userDetails.github = githubMatch[1];
      const locationMatch = line.match(patterns.location);
      if (locationMatch && !userDetails.location)
        userDetails.location = locationMatch[1];
    }

    // Summary extraction
    const summaryHeaders = ["SUMMARY", "OBJECTIVE", "PROFILE", "ABOUT"];
    let summaryStartIndex = -1;
    let summaryEndIndex = -1;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (
        summaryHeaders.some((header) => line.toUpperCase().includes(header))
      ) {
        summaryStartIndex = i + 1;
        for (let j = summaryStartIndex; j < lines.length; j++) {
          if (
            lines[j]
              .toUpperCase()
              .match(
                /^(EXPERIENCE|EDUCATION|SKILLS|PROJECTS|CERTIFICATES|AWARDS|INTERESTS)/
              )
          ) {
            summaryEndIndex = j;
            break;
          }
        }
        if (summaryEndIndex === -1) summaryEndIndex = lines.length;
        break;
      }
    }
    if (summaryStartIndex !== -1) {
      userDetails.summary = lines
        .slice(summaryStartIndex, summaryEndIndex)
        .join(" ")
        .trim();
    }

    // Skills extraction
    const cleanedText = text.replace(/^\s*\|+\s*$/gm, "");
    const skillsSectionMatch = cleanedText.match(
      /Skills\s*([\s\S]*?)(?=\r?\n\s*(Edu(?:cation)?|Projects|Certificates|Experience|Languages|Achievements|Work Experience|Internships)\b)/i
    );

    if (skillsSectionMatch && skillsSectionMatch[1]) {
      const cleanedSkillsText = skillsSectionMatch[1]
        .replace(/\n/g, ",")
        .replace(/[•|]/g, ",")
        .replace(/[\u2013\u2014–—]/g, ",")
        .trim();

      userDetails.skills = cleanedSkillsText
        .split(/[,:\-]+/)
        .map((skill) => skill.trim())
        .filter(
          (skill) =>
            skill.length > 1 &&
            !skill.match(
              /^(Skills?|Programming|Languages?|Web|Development|Frameworks?|Tools?|Data|Science|Visualization|Databases?|and)$/i
            )
        );
    }

    // Certifications extraction
    const certsSectionMatch = text.match(
      /CERTIFICATES?(.*?)(?=\n\s*\n|\n[A-Z\s]{4,}|$)/is
    );
    if (certsSectionMatch && certsSectionMatch[1]) {
      userDetails.certifications = certsSectionMatch[1]
        .split(/[,•-]\s*|\s*\n\s*/i)
        .map((cert) => cert.replace(/—|\u2014/, "").trim())
        .filter(
          (cert) =>
            cert.length > 5 &&
            !cert.match(/^(SKILLS|EXPERIENCE|EDUCATION|PROJECTS|LANGUAGES)$/i)
        );
    }

    return userDetails;
  };

  // User profile rendering
  const renderUserProfile = (userDetails, resumeContent) => {
    return (
      <div className="bg-white p-6 rounded-lg ">
        {/* Name */}
        <div className="mb-4">
          <h3 className="text-2xl font-bold text-gray-900">
            {userDetails.name || "Name Not Found"}
          </h3>
        </div>
        {/* Contact Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-y-2 mb-6 text-gray-700 text-sm break-words">
          {userDetails.email && (
            <p className="flex items-center break-all">
              <FaEnvelope className="mr-2 text-gray-500" />
              <span className="break-all">{userDetails.email}</span>
            </p>
          )}
          {userDetails.phone && (
            <p className="flex items-center break-all">
              <FaPhone className="mr-2 text-gray-500" />
              <span className="break-all">{userDetails.phone}</span>
            </p>
          )}
          {userDetails.location && (
            <p className="flex items-center break-all">
              <FaMapMarkerAlt className="mr-2 text-gray-500" />
              <span className="break-all">{userDetails.location}</span>
            </p>
          )}
          {userDetails.linkedin && (
            <a
              href={
                userDetails.linkedin.startsWith("http")
                  ? userDetails.linkedin
                  : `https://${userDetails.linkedin}`
              }
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center text-blue-600 hover:underline break-all"
              style={{ wordBreak: "break-all" }}
            >
              <FaLinkedin className="mr-2" />
              <span className="break-all">LinkedIn</span>
            </a>
          )}
          {userDetails.github && (
            <a
              href={
                userDetails.github.startsWith("http")
                  ? userDetails.github
                  : `https://${userDetails.github}`
              }
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center text-gray-800 hover:underline break-all"
              style={{ wordBreak: "break-all" }}
            >
              <FaGithub className="mr-2" />
              <span className="break-all">GitHub</span>
            </a>
          )}
          {userDetails.portfolio && (
            <a
              href={
                userDetails.portfolio.startsWith("http")
                  ? userDetails.portfolio
                  : `https://${userDetails.portfolio}`
              }
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center text-purple-600 hover:underline break-all"
              style={{ wordBreak: "break-all" }}
            >
              <FaLink className="mr-2" />
              <span className="break-all">Portfolio</span>
            </a>
          )}
        </div>
        {/* Summary */}
        {userDetails.summary && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Summary
            </h3>
            <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-line">
              {userDetails.summary}
            </p>
          </div>
        )}
        {/* Skills */}
        {userDetails.skills && userDetails.skills.length > 0 && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Skills</h3>
            <div className="flex flex-wrap gap-2">
              {userDetails.skills.map((skill, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-indigo-50 text-indigo-700 rounded-full text-sm"
                >
                  {skill}
                </span>
              ))}
            </div>
          </div>
        )}
        {/* Languages */}
        {userDetails.languages && userDetails.languages.length > 0 && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Languages
            </h3>
            <div className="flex flex-wrap gap-2">
              {userDetails.languages.map((language, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                >
                  {language}
                </span>
              ))}
            </div>
          </div>
        )}
        {/* Certifications */}
        {userDetails.certifications &&
          userDetails.certifications.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Certifications
              </h3>
              <ul className="list-disc list-inside text-gray-600 text-sm">
                {userDetails.certifications.map((cert, index) => (
                  <li key={index} className="flex items-start">
                    <svg
                      className="w-4 h-4 text-green-500 mr-2 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      ></path>
                    </svg>
                    <span>{cert}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        {/* Experience */}
        {resumeContent.experience && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Experience
            </h3>
            <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-line">
              {resumeContent.experience}
            </p>
          </div>
        )}
        {/* Projects */}
        {resumeContent.projects && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Projects
            </h3>
            <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-line">
              {resumeContent.projects}
            </p>
          </div>
        )}
        {/* Education */}
        {resumeContent.education && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Education
            </h3>
            <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-line">
              {resumeContent.education}
            </p>
          </div>
        )}
      </div>
    );
  };

  // Drop handler
  const onDrop = useCallback(
    async (acceptedFiles) => {
      const file = acceptedFiles[0];

      if (!selectedRole) {
        setError("Please select a role first.");
        return;
      }

      if (!experience) {
        setError("Please enter your years of experience.");
        return;
      }

      if (file?.type === "application/pdf") {
        setLoading(true);
        setError("");
        setAiResult(null);

        try {
          // Step 1: Read PDF file
          const text = await readPdfFile(file);
          const cleanedText = text
            .replace(/[ \t]+/g, " ")
            .replace(/\r\n|\r/g, "\n")
            .replace(/\n{2,}/g, "\n")
            .trim();

          // Step 2: Extract sections and user details
          const sections = extractSections(text);
          const userDetails = extractUserDetails(text);
          setResumeContent(sections);
          setUserDetails(userDetails);

          // Step 3: Build prompt
          const prompt = `
          You are a hiring expert. I'm sharing my resume and a job role. Please analyze how well my skills and experience align with the role. Identify matches, gaps, and suggest improvements to better fit the role.
          Please respond in a structured and detailed way,but also respond ONLY in the following JSON format:
          {
          "skillMatches": [...],
          "gaps": [...],
          "suggestions": [...],
          "score": 85
          }

        🔹 Here is my resume:
        ${text}

        🔹 Here is the job role/description:
        Role: ${selectedRole}
        Experience: ${experience} years

        Please provide:
        1. Skill matches
        2. Gaps or missing experience
        3. Suggestions to tailor or improve the resume for this role
        4. Overall match score out of 100`;

          // --- CACHE CHECK ---
          if (
            lastAiRequest.current.text === cleanedText &&
            lastAiRequest.current.prompt === prompt &&
            lastAiRequest.current.result
          ) {
            console.log("Using cached AI result");
            setAiResult(lastAiRequest.current.result);
            setCurrentStep(4);
            setLoading(false);
            return;
          }

          // Step 4: Call Gemini AI
          const ai = getAI(app, { backend: new GoogleAIBackend() });
          const model = getGenerativeModel(ai, { model: "gemini-1.5-flash" });
          const response = await model.generateContent(prompt);
          let aiText = await response.response.text();

          let parsedResult = null;
          try {
            const jsonMatch = aiText.match(/```json\n([\s\S]*?)\n```/);
            let cleanAiText = jsonMatch ? jsonMatch[1].trim() : aiText;
            parsedResult = JSON.parse(cleanAiText);
          } catch (parseError) {
            parsedResult = aiText;
          }
          setAiResult(parsedResult);
          setCurrentStep(4);

          // --- SAVE TO CACHE ---
          lastAiRequest.current = {
            text: cleanedText,
            prompt,
            result: parsedResult,
          };
        } catch (error) {
          setError("Error reading PDF or analyzing resume.");
        } finally {
          setLoading(false);
        }
      } else {
        setError("Please upload a valid PDF file.");
      }
    },
    [selectedRole, experience]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "application/pdf": [".pdf"] },
    multiple: false,
  });

  // Step content rendering (UI)
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="mt-8 animate-fadeIn">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              Select Your Role
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Object.entries(INDUSTRY_ROLES).map(([industry, roles]) => (
                <div key={industry} className="bg-white rounded-lg shadow p-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-3">
                    {industry}
                  </h3>
                  <div className="space-y-2">
                    {roles.map((role) => (
                      <button
                        key={role}
                        onClick={() => {
                          setSelectedRole(role);
                          setCurrentStep(2);
                        }}
                        className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors ${
                          selectedRole === role
                            ? "bg-indigo-600 text-white"
                            : "bg-gray-50 text-gray-700 hover:bg-gray-100"
                        }`}
                      >
                        {role}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      case 2:
        return (
          <div className="mt-8 animate-fadeIn">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              Enter Your Years of Experience
            </h2>
            <div className="max-w-md mx-auto">
              <label
                htmlFor="experience"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                How many years of professional experience do you have in the
                selected role?
              </label>
              <input
                type="number"
                id="experience"
                name="experience"
                value={experience}
                onChange={(e) => setExperience(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="e.g., 5"
                min="0"
              />
              <div className="mt-6 flex justify-between">
                <button
                  onClick={() => setCurrentStep(1)}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                >
                  Back
                </button>
                <button
                  onClick={() => setCurrentStep(3)}
                  disabled={!experience}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        );
      case 3:
        return (
          <div className="mt-8 animate-fadeIn">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              Upload Your Resume
            </h2>
            <div className="mb-4 flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">
                  Selected Role:{" "}
                  <span className="font-medium text-gray-900">
                    {selectedRole}
                  </span>
                </p>
                <p className="text-sm text-gray-600">
                  Experience:{" "}
                  <span className="font-medium text-gray-900">
                    {experience} years
                  </span>
                </p>
                <button
                  onClick={() => setCurrentStep(1)}
                  className="text-sm text-indigo-600 hover:text-indigo-800 mt-1"
                >
                  Change Role or Experience
                </button>
              </div>
            </div>
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                isDragActive
                  ? "border-indigo-500 bg-indigo-50"
                  : "border-gray-300 hover:border-indigo-500"
              }`}
            >
              <input {...getInputProps()} />
              <div className="space-y-4">
                <div className="flex justify-center">
                  <svg
                    className="h-12 w-12 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                  </svg>
                </div>
                <div className="text-sm text-gray-600">
                  {isDragActive ? (
                    <p>Drop your resume here</p>
                  ) : (
                    <div className="space-y-1">
                      <p>
                        Drag and drop your resume, or{" "}
                        <span className="text-indigo-600 font-medium">
                          browse
                        </span>
                      </p>
                      <p className="text-xs text-gray-500">
                        PDF files only (max 10MB)
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {loading && (
              <div className="mt-4 p-4 rounded-md bg-blue-50 text-blue-700">
                <div className="flex items-center">
                  <div className="mr-3 animate-spin rounded-full h-4 w-4 border-2 border-current border-t-transparent"></div>
                  <p>Processing and analyzing resume...</p>
                </div>
              </div>
            )}
            {error && (
              <div className="mt-4 p-4 rounded-md bg-red-50 text-red-700">
                <p>{error}</p>
              </div>
            )}
          </div>
        );
      case 4:
        return (
          <>
            <h1 className="text-2xl font-bold text-center mb-6">
              🎯 Resume Analysis Results
            </h1>
            {/* Circular Progress Bar */}
            {aiResult?.score != null && (
              <div className="flex justify-center my-8">
                <div className="w-48 h-48">
                  <CircularProgressbarWithChildren
                    value={
                      typeof aiResult.score === "number"
                        ? aiResult.score
                        : Number(aiResult.score)
                    }
                    styles={buildStyles({
                      pathColor: "#4f46e5",
                      trailColor: "#e5e7eb",
                    })}
                  >
                    <div
                      style={{
                        textAlign: "center",
                        fontSize: 25,
                        color: "#4f46e5",
                      }}
                    >
                      <strong style={{ fontSize: 25 }}>
                        {`${Number(aiResult.score)}%`}
                      </strong>
                      <div>score</div>
                    </div>
                  </CircularProgressbarWithChildren>
                </div>
              </div>
            )}
            {/* Start Over button and messages */}
            <div className="flex flex-col items-center mb-6">
              <button
                onClick={() => setCurrentStep(1)}
                className="mb-4 px-6 py-3 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
              >
                Start Over
              </button>
              {loading && <p className="text-indigo-600">Analyzing...</p>}
              {error && <p className="text-red-500">{error}</p>}
            </div>
            {/* Two-column layout for Profile and AI Analysis */}
            <div className="flex flex-col-reverse md:flex-row gap-8 mt-8">
              {/* Left: User Profile */}
              <div className="bg-white p-8 rounded-lg shadow-xl md:w-1/2">
                <h2 className="text-2xl font-bold text-gray-800 mb-6">
                  👤 Your Profile
                </h2>
                {userDetails ? (
                  renderUserProfile(userDetails, resumeContent)
                ) : (
                  <p className="text-gray-600">
                    Could not extract user details from resume.
                  </p>
                )}
              </div>
              {/* Right: AI Analysis */}
              <div className="bg-white p-8 rounded-lg shadow-xl md:w-1/2">
                <h2 className="text-2xl font-bold text-gray-800 mb-6">
                  🧠 AI Analysis
                </h2>
                {/* Skill Matches */}
                {aiResult?.skillMatches && (
                  <div className="mb-8 p-6 bg-green-50 border border-green-200 rounded-lg shadow-sm">
                    <h3 className="text-xl font-semibold text-green-800 mb-4">
                      ✅ Skill Matches
                    </h3>
                    <ul className="list-disc list-inside text-gray-700 space-y-2">
                      {aiResult.skillMatches.map((match, idx) => (
                        <li key={idx} className="flex items-start">
                          <span className="mr-2 text-green-600">•</span>
                          <span>{match}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {/* Gaps */}
                {aiResult?.gaps && (
                  <div className="mb-8 p-6 bg-red-50 border border-red-200 rounded-lg shadow-sm">
                    <h3 className="text-xl font-semibold text-red-800 mb-4">
                      ⚠️ Gaps / Missing Skills
                    </h3>
                    <ul className="list-disc list-inside text-gray-700 space-y-2">
                      {aiResult.gaps.map((gap, idx) => (
                        <li key={idx} className="flex items-start">
                          <span className="mr-2 text-red-600">•</span>
                          <span>{gap}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {/* Suggestions */}
                {aiResult?.suggestions && (
                  <div className="mb-8 p-6 bg-blue-50 border border-blue-200 rounded-lg shadow-sm">
                    <h3 className="text-xl font-semibold text-blue-800 mb-4">
                      📌 Suggestions
                    </h3>
                    <ul className="list-disc list-inside text-gray-700 space-y-2">
                      {aiResult.suggestions.map((tip, idx) => (
                        <li key={idx} className="flex items-start">
                          <span className="mr-2 text-blue-600">•</span>
                          <span>{tip}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {/* Fallback if AI gives plain text instead of JSON */}
                {typeof aiResult === "string" && (
                  <div className="bg-gray-50 p-6 mt-4 rounded-lg text-sm text-gray-800 whitespace-pre-line max-h-96 overflow-y-auto border border-gray-200 shadow-sm break-words">
                    <h3 className="text-xl font-semibold text-gray-800 mb-4">
                      ❗ Raw AI Response (Not JSON)
                    </h3>
                    <div
                      className="break-words"
                      style={{
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                      }}
                      dangerouslySetInnerHTML={{
                        __html: aiResult
                          .replace(
                            /^##\s(.+)/gm,
                            (_, title) => `<h2>${title.trim()}</h2>`
                          )
                          .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
                          .replace(/^\*\s(.+)/gm, "<li>$1</li>")
                          .replace(/(<li>.*?<\/li>)/gs, "<ul>$1</ul>")
                          .replace(/\n/g, "<br/>"),
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div className="mx-auto p-6 max-w-full">
      {/* Progress Steps */}
      <div className="mb-12">
        <div className="flex flex-col sm:flex-row items-center justify-center">
          {[
            { step: 1, label: "Select Role" },
            { step: 2, label: "Enter Experience" },
            { step: 3, label: "Upload Resume" },
            { step: 4, label: "Review Results" },
          ].map((item, index) => (
            <React.Fragment key={item.step}>
              <div className="flex flex-col items-center sm:relative sm:flex-col">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center mb-1 sm:mb-0 ${
                    currentStep >= item.step
                      ? "bg-indigo-600 text-white"
                      : "bg-gray-200 text-gray-600"
                  }`}
                >
                  {item.step}
                </div>
                <div
                  className={
                    "mt-1 text-xs sm:text-sm text-center sm:absolute sm:-bottom-6 sm:left-1/2 sm:-translate-x-1/2 sm:w-max " +
                    (currentStep >= item.step
                      ? "text-indigo-600"
                      : "text-gray-500")
                  }
                  style={{ minWidth: "60px" }}
                >
                  {item.label}
                </div>
              </div>
              {index < 3 && (
                <div className="hidden sm:block w-24 mx-2 h-0.5">
                  <div
                    className={`h-full ${
                      currentStep > item.step ? "bg-indigo-600" : "bg-gray-200"
                    }`}
                  ></div>
                </div>
              )}
              {index < 3 && (
                <div className="block sm:hidden w-1 h-6 bg-gray-200 mx-auto my-1"></div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
      {/* Step Content */}
      {renderStepContent()}
    </div>
  );
};

export default ResumeAnalyzer;
